import React from "react";
import { motion } from "framer-motion";
import { IKImage } from "imagekitio-react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "./RoomCard.css";

interface IProps {
  path: string;
  name: string;
  text: string;
  style?: React.CSSProperties;
  ribbon?: string;
  href?: string;
}

const RoomCard = (props: IProps) => {
  const urlEndpoint = "https://ik.imagekit.io/kimchel";
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <motion.div
      className="roomCard"
      initial="rest"
      whileHover="hover"
      animate="rest"
      variants={{
        rest: { y: 0, boxShadow: "0 8px 28px rgba(0,0,0,0.25)" },
        hover: { y: -8, boxShadow: "0 20px 48px rgba(0,0,0,0.4)" },
      }}
      transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      onClick={() => props.href && navigate(props.href)}
      style={{ cursor: props.href ? "pointer" : "default" }}
    >
      {/* Corner ribbon */}
      {props.ribbon && (
        <div className="roomCard-ribbon">
          <span>{props.ribbon}</span>
        </div>
      )}

      {/* Image — zooms on hover via variant propagation */}
      <motion.div
        className="roomCard-imageWrapper"
        variants={{
          rest: { scale: 1 },
          hover: { scale: 1.07 },
        }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <IKImage
          urlEndpoint={urlEndpoint}
          path={props.path}
          className="roomCard-image"
          alt={t(props.name)}
        />
      </motion.div>

      {/* Always-on bottom gradient */}
      <div className="roomCard-gradient" />

      {/* Text panel */}
      <div className="roomCard-panel">
        {/* Title — always visible */}
        <h3 className="roomCard-title">
          <Trans i18nKey={props.name} />
        </h3>

        {/* Description — fades + slides up on hover */}
        <motion.p
          className="roomCard-desc"
          variants={{
            rest: { opacity: 0, y: 12, height: 0, marginTop: 0 },
            hover: { opacity: 1, y: 0, height: "auto", marginTop: 8 },
          }}
          transition={{ duration: 0.35, ease: "easeOut" }}
        >
          <Trans i18nKey={props.text} />
        </motion.p>

        {/* Explore hint */}
        <motion.span
          className="roomCard-explore"
          variants={{
            rest: { opacity: 0, x: -6 },
            hover: { opacity: 1, x: 0 },
          }}
          transition={{ duration: 0.3, delay: 0.08, ease: "easeOut" }}
        >
          {t("roomCard.explore")} →
        </motion.span>
      </div>
    </motion.div>
  );
};

export default RoomCard;
